
import { useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { Section, WebhookConfig } from '@/types/sections';
import { showSuccessToast } from '@/utils/toastHelpers';
import { useTranslation } from 'react-i18next';

interface UseSectionsManagerProps {
  sections: Section[];
  onSectionsChange: (sections: Section[]) => void;
  webhookConfig?: WebhookConfig | null;
  onWebhookConfigChange?: (config: WebhookConfig) => void;
}

export const useSectionsManager = ({
  sections,
  onSectionsChange,
  webhookConfig,
  onWebhookConfigChange
}: UseSectionsManagerProps) => {
  const { t } = useTranslation();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = sections.findIndex((item) => item.id.toString() === active.id);
      const newIndex = sections.findIndex((item) => item.id.toString() === over.id);
      const reorderedSections = arrayMove(sections, oldIndex, newIndex);
      
      const sectionsWithUpdatedOrder = reorderedSections.map((section, index) => ({
        ...section,
        order: index + 1
      }));
      
      onSectionsChange(sectionsWithUpdatedOrder);
    }
  };

  const handleSectionToggle = (sectionId: number, checked: boolean) => {
    const updatedSections = sections.map((section) =>
      section.id === sectionId ? { ...section, checked } : section
    );
    onSectionsChange(updatedSections);
  };

  const handleEditCard = (section: Section) => {
    setSelectedSection(section);
    setEditDialogOpen(true);
  };

  const handleDeleteCard = (section: Section) => {
    setSelectedSection(section);
    setDeleteDialogOpen(true);
  };

  const handleEditSave = (updatedSection: Section) => {
    const updatedSections = sections.map((section) =>
      section.id === updatedSection.id ? updatedSection : section
    );
    onSectionsChange(updatedSections);
  };

  const handleDeleteConfirm = () => {
    if (selectedSection) {
      const updatedSections = sections.filter((section) => section.id !== selectedSection.id);
      onSectionsChange(updatedSections);
    }
  };

  const handleAddSection = (newSectionData: Omit<Section, 'id' | 'order'>) => {
    const maxOrder = Math.max(...sections.map(s => s.order), 0);
    
    // Create new section without ID - let the backend assign it
    const newSection = {
      ...newSectionData,
      order: maxOrder + 1,
      date: null,
    };
    console.log('Adding new section (without ID):', newSection);
    
    // Create the new sections array with the new section
    const newSections = [...sections, newSection as Section];
    
    // Call onSectionsChange to trigger the API call
    onSectionsChange(newSections);
    
    // Close the dialog
    setAddDialogOpen(false);
    
    // Show success toast with translated text
    const addSectionText = t('add_new_section');
    showSuccessToast('add_new_section', `${addSectionText} - ${newSection.title}`);
  };

  const handleWebhooksClick = () => {
    setWebhookDialogOpen(true);
  };

  const handleWebhookSave = (config: WebhookConfig) => {
    console.log('Webhook configuration saved:', config);
    if (onWebhookConfigChange) {
      onWebhookConfigChange(config);
    }
    setWebhookDialogOpen(false);
    showSuccessToast('configuration_saved_successfully', 'Configuration saved successfully');
  };

  return {
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    addDialogOpen,
    setAddDialogOpen,
    webhookDialogOpen,
    setWebhookDialogOpen,
    selectedSection,
    handleDragEnd,
    handleSectionToggle,
    handleEditCard,
    handleDeleteCard,
    handleEditSave,
    handleDeleteConfirm,
    handleAddSection,
    handleWebhooksClick,
    handleWebhookSave,
  };
};
